<template>
  <v-card>
    <v-card-title>
      Enter a Release# to request the material
    </v-card-title>
    <v-card-text>
      <v-row>
        <v-col>
          <v-autocomplete
            v-model="jobId"
            :items="jobs"
            hide-details="true"
            item-value="id"
            :item-text="item => `${item.code} - ${item.name}; ${item.reference}`"
            clearable
            :loading="dataIsLoading"
          >
            <template #selection="{ item }">
              <span>
                <span style="font-weight: bold;">{{ item.code ? `${item.code}` : '' }}</span> - {{ item.name ? item.name : '' }} <span style="color: #1976D2;">{{ item.reference ? `; ${item.reference}` : '' }}</span>
              </span>
            </template>
            <template #label>
              {{ jobLabel }}<red-asterisk />
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>

      <v-row class="mt-2">
        <v-col cols="6">
          <inventory-class-select-element v-model="classId"/>
        </v-col>
        <v-col cols="6">
          <v-autocomplete 
            v-model="subclassIds" 
            label="Sub Classes"
            :items="filteredSubClasses"
            item-text="name"
            item-value="id"
            :loading="dataIsLoading"
            multiple
          >
            <template #item="{ item }">
              <v-chip
                class="mt-1 mb-1"
                small
                text-color="white"
                style="cursor: pointer;"
                :color="item ? item.color : 'white'"
              >
                {{ item ? item.name : '' }}
              </v-chip>
            </template>
            <template #selection="{ item }">
              <v-chip
                class="mt-1 mb-1"
                small
                text-color="white"
                style="cursor: pointer;"
                :color="item ? item.color : 'white'"
              >
                {{ item ? item.name : '' }}
              </v-chip>
            </template>
        </v-autocomplete>
        </v-col>
      </v-row>
      <v-row class="mt-2">
        <v-col cols="8">
          <v-autocomplete 
            v-model="inventoryItemId" 
            :items="inventoryItems" 
            item-value="id" 
            :item-text="item => `${item.material_id}: ${item.name}`" 
            hide-details 
            class="pt-0" 
            :loading="dataIsLoading"
          >
            <template #label>Inventory Item<red-asterisk/></template>
            <template #item="{ item }">
              <span class="font-weight-bold">{{ item.material_id }}</span>:
              {{ item.name }}
            </template>
            <template #selection="{ item }">
              <span class="font-weight-bold">{{ item.material_id }}</span>:
              {{ item.name }}
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="4">
          <v-autocomplete 
            v-model="colorId" 
            label="Color" 
            :items="colors" 
            item-value="id" 
            :loading="dataIsLoading"
            hide-details 
            class="pt-0">
            <template #item="{ item }">
              {{ item.code }}: {{ item.name }}
            </template>
            <template #selection="{ item }">
              {{ item.code }}: {{ item.name }}
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="4">
          <v-autocomplete v-model="lengthId" :items="itemLengths" item-value="id" item-text="name" hide-details class="pt-0">
            <template #label>Length</template>
          </v-autocomplete>
        </v-col>
        <v-col cols="4">
          <v-autocomplete v-model="uomId" :items="itemUoms" item-value="id" item-text="name" hide-details class="pt-0" :loading="dataIsLoading">
            <template #label>UOM<red-asterisk/></template>
          </v-autocomplete>
        </v-col>
        <v-col cols="4">
          <v-text-field v-model="qtyRequired" hide-details class="pt-0">
            <template #label>Quantity Required<red-asterisk/></template>
          </v-text-field>
        </v-col>

        <v-col cols="6">
          <v-select :items="reasons" item-text="name" item-value="id" v-model="reasonId">
            <template #label>Reason <red-asterisk/></template>
          </v-select>
        </v-col>
        <v-col cols="6">
          <v-select :items="subReasons" item-text="name" item-value="id" v-model="subReasonId">
            <template #label>Sub Reason</template>
          </v-select>
        </v-col>
      </v-row>

    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn color="normal" class="mr-2 ml-10" @click="close">
        CLOSE
      </v-btn>
      <v-btn color="primary" @click="submit">
        SUBMIT
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex';
import * as allocationApi from '../../../api/InventoryAllocationApi';
import RedAsterisk from '../../RedAsterisk.vue';

export default {
  components: {
    RedAsterisk
  },
  emits: ['close'],
  data: () => ({
    jobs: [],
    classes: [],
    subClasses: [],
    filteredSubClasses: [],
    classToSubclassesMap: {},
    inventoryItems: [],
    colors: [],
    uoms: [],
    reasons: [],
    reasonIdToSubreasonsMap: {},

    jobId: null,
    classId: null,
    subclassIds: [],

    inventoryItemId: null,
    colorId: null,
    uomId: null,
    lengthId: null,
    reasonId: null,
    subReasonId: null,
    qtyRequired: null,

    dataIsLoading: false,
  }),
  computed: {
    ...mapGetters([
      'businessRulesGetBoolean',
    ]),
    jobLabel() {
      return this.businessRulesGetBoolean('Jobs Rename to Release') ? 'Release' : 'Job';
    },
    subReasons() {
      if (this.reasonId === null || !(this.reasonId in this.reasonIdToSubreasonsMap)) return [];
      return this.reasonIdToSubreasonsMap[this.reasonId];
    },
    itemUoms() {
      if (!this.inventoryItemId) return [];
      return this.inventoryItems.find(x => x.id === this.inventoryItemId)?.uom_items ?? [];
    },
    itemLengths() {
      if (!this.inventoryItemId) return [];
      return this.inventoryItems.find(x => x.id === this.inventoryItemId)?.length_items ?? [];
    }
  },
  watch: {
    classId(value) {
      this.filteredSubClasses = value ? this.classToSubclassesMap[value] : this.subClassesList;
      if (value !== null && value !== 0) this.subclassIds = this.subclassIds.filter(x => this.filteredSubClasses.findIndex(y => x === y.id) > -1);
      this.getInventoryItems();
    },
    subclassIds() {
      this.getInventoryItems();
    },
    inventoryItemId() {
      const inventoryItem = this.inventoryItems.find(x => x.id === this.inventoryItemId);
      if (!inventoryItem) return;
      this.classId = inventoryItem.inventory_class_id;
      if (inventoryItem.inventory_sub_classes) this.subclassIds = inventoryItem.inventory_sub_classes.map(x => x.id);
      else this.subclassIds = [];
    }
  },
  created() {
    this.dataIsLoading = true;

    Promise.all([
      this.$api.job.readDDL({}),
      this.$api.color.read(),
      this.$api.uom.getList(),
      this.$api.inventorySubClass.getList(),
      allocationApi.getRequestMaterialReasons()
    ]).then(res => {
      this.jobs = res[0];
      this.colors = res[1];
      this.uoms = res[2];
      this.subClasses = res[3];
      this.reasons = res[4];
      this.reasonIdToSubreasonsMap = this.reasons.reduce((acc, x) => {
        acc[x.id] = x.sub_reasons;
        return acc;
      }, {})
      this.classToSubclassesMap = this.subClasses.reduce((acc, x) => {
        if (!(x.inventory_class_id in acc)) acc[x.inventory_class_id] = [];
        acc[x.inventory_class_id].push(x);
        return acc;
      }, {});
      this.filteredSubClasses = this.classId ? this.classToSubclassesMap[this.classId] : this.subClassesList;
      this.dataIsLoading = false;
    })
  },
  methods: {
    async submit() {
      if (!this.jobId || !this.inventoryItemId || !this.uomId || !this.reasonId || !this.qtyRequired) {
        return this.$bus.$emit('showError', 'Please complete the form');
      }
      const data = {
        job_id: this.jobId,
        inventory_item_id: this.inventoryItemId,
        uom_id: this.uomId,
        length_id: this.lengthId,
        color_id: this.colorId,
        reason_id: this.reasonId,
        sub_reason_id: this.subReasonId,
        qty_required: this.qtyRequired
      }
      try {
        const res = await allocationApi.requestMaterial(data);
        this.$bus.$emit('showSuccess', 'Material has been requested');
        this.$emit('on-material-requested');
        this.close();
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', `Failed to request material. ${e.response?.data || ''}`);
      }
    },
    async getInventoryItems() {
      const filters = {
        inventory_class_id: this.classId,
        inventory_sub_class_ids: this.subclassIds
      };
      const res = await this.$api.inventoryItem.readData({ filters });
      this.inventoryItems = res.data || [];
    },
    reset() {
      this.classId = null;
      this.subclassIds = [];
      this.inventoryItemId = null;
      this.uomId = null;
      this.lengthId = null;
      this.colorId = null;
      this.reasonId = null;
      this.subReasonId = null;
      this.qtyRequired = null;
      this.jobId = null;
    },
    close() {
      this.reset();
      this.$emit('close');
    }
  },
};
</script>